/**
 * @typedef {import('../../shared/OfferService/OffersProps').default} Props
 * @typedef {import('../../shared/OfferService/OffersState').ViewGroupOffersState} State
 * @typedef {import('./OfferFields').default} OfferFields
 */
import { AuthContext } from '../../../shared/AuthProvider';
import { translate } from '../../Internationalization';
import { withOfferService } from '../../../service-container';
import BaseTable from '../../../shared/BaseTable';
import captureError from '../../../utils/captureError';
import categoryList from '@mooveguru/yhh-shared-config/marketplace-categories.json';
import images from '../../../config/local/images';
import LinkButton from '../../../shared/LinkButton';
import Loading from '../../../shared/Loading';
import Pagination from '../../../shared/Pagination';
import paths from '../../../config/local/paths';
import React from 'react';
import TableButton from '../../../shared/TableButton';
import VendorExclusivity from '../../shared/VendorExclusivity';

/** @extends {React.Component<Props, State>} */
export class ViewGroupOffers extends React.Component {
	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			currentPageNumber: 1,
			isGettingData: true,
			lastPageNumber: 1,
			offers: [],
		};

		this.setTableData = this.setTableData.bind(this);
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		try {
			await this.setTableData();
		} catch (error) {
			captureError(error);

			this.setState({
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {number} pageNumber
	 * @returns {Promise<void>}
	 */
	async setTableData(pageNumber = 1) {
		try {
			const response = await this.props.offerService.getGroupOffers(
				pageNumber
			);

			this.setState({
				currentPageNumber: pageNumber,
				isGettingData: false,
				lastPageNumber: response.pages ?? 1,
				offers: response.offers,
			});
		} catch (error) {
			captureError(error);

			this.setState({
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {string} category
	 * @returns {string}
	 */
	static #resolveCategory(category) {
		// @ts-ignore category can potentially not be in JSON category list
		return categoryList[category] ?? category;
	}

	/**
	 * @returns {JSX.Element}
	 */

	render() {
		if (this.state.isGettingData) {
			return <Loading />;
		}

		const headerKeyList = [
			'vendor_name',
			'vendor_category',
			'vendor_desc',
			'vendor_zip',
			'status',
			'edit',
		];

		const tableHeaderData = headerKeyList.map((key) => ({
			title: translate(`agent.pages.offers.view.table.${key}`),
		}));

		const tableBodyData = this.state.offers.map((offer, index) => [
			{
				data: offer.vendorName,
				index: `contact-${index}`,
			},
			{
				data: ViewGroupOffers.#resolveCategory(offer.offerCategory),
				index: `contact-${index}`,
			},
			{
				data: offer.offerDescription,
				index: `contact-${index}`,
			},
			{
				data: offer.offerZip,
				index: `contact-${index}`,
			},
			{
				data: offer.status,
				index: `contact-${index}`,
			},
			{
				data: (
					<TableButton
						icon={images.icons.edit}
						to={{
							pathname: paths.app.admin.offers.edit,
							state: { offerId: offer.id },
						}}
					/>
				),
			},
		]);

		return (
			<React.Fragment>
				<header className="mt-10 mb-6 md:flex justify-between items-center">
					<h1 className="hl-ms-6 mb-6 md:mb-0">
						{translate('admin.pages.offers.groups_view.title')}
					</h1>
					<section className="button-group">
						<LinkButton
							icon={images.icons.eye}
							to={paths.app.admin.offers.view}
						>
							{this.context.roles.includes('manager')
								? translate(
										'admin.pages.offers.view.view_agent'
								  )
								: translate(
										'admin.pages.offers.view.view_domain'
								  )}
						</LinkButton>
						<LinkButton
							icon={images.icons.plus}
							to={paths.app.admin.offers.add}
						>
							{translate('global.offers.add_title')}
						</LinkButton>
					</section>
				</header>

				{/* @ts-ignore */}
				<VendorExclusivity type="administrator" />

				<BaseTable
					bodyData={tableBodyData}
					className="w-full"
					errorMessage={translate('admin.pages.offers.view.no_data')}
					headerData={tableHeaderData}
				/>

				<Pagination
					currentPageNumber={this.state.currentPageNumber}
					lastPageNumber={this.state.lastPageNumber}
					setCurrentPageNumber={this.setTableData}
				/>
			</React.Fragment>
		);
	}
}

ViewGroupOffers.contextType = AuthContext;

export default withOfferService(ViewGroupOffers);
